import { resolveSchema } from '@jsonforms/core';
import type { AnnotatedJsonSchema } from '@web-config-app/core';

export interface ResolveArraySchemaArgs {
  /** Root JSON Schema */
  rootSchema: AnnotatedJsonSchema;
  /** Data path in dot notation (e.g. "campaignNodes.0.activitySets.2.activities"). */
  path: string;
}

/**
 * Convert a dot path (with numeric indices) to a JSON Pointer suited for schema resolution.
 * Numbers become '/items'; names become '/properties/<name>'.
 */
const dataPathToJsonPointer = (dataPath: string): string => {
  if (!dataPath) return '#';
  const segments = dataPath.split('.');
  const pointerParts: string[] = ['#'];
  segments.forEach((seg) => {
    if (/^\d+$/.test(seg)) {
      pointerParts.push('items');
    } else {
      pointerParts.push('properties', seg);
    }
  });
  return pointerParts.join('/');
};

/**
 * resolveArraySchema
 * WHY: JSON Forms supplies array renderers with the item schema. We need the array container
 * schema for: button labels, flattened layout logic, localization scans, and annotations.
 * HOW: Build a JSON Pointer from the dot path (numeric segments -> items). Resolve it directly.
 * If the resolved schema is an array, return it. Otherwise (including primitive/object leaves)
 * return the resolved schema (fail‑soft, never throw). Indices are always translated via /items,
 * so a single pass is sufficient for current needs.
 */
export const resolveArraySchema = ({
  rootSchema,
  path,
}: ResolveArraySchemaArgs): AnnotatedJsonSchema => {
  const jsonPointer = dataPathToJsonPointer(path);
  return resolveSchema(rootSchema, jsonPointer, rootSchema);
};
